import i18n from 'i18n/config';

/**
 * Typed failure of the full tenant-config save (`POST /config/tenant/{id}`).
 *
 * `message` is the translated main message, chosen by status, so existing
 * `snackbar(err.message)` callers show it unchanged. The server's own English
 * `detail` and `validation_errors` are kept separately as technical details.
 */
export type ConfigSaveErrorKind = 'missing_hash' | 'forbidden' | 'conflict' | 'invalid';

/** One server-side validation error: `{loc: [...], msg}`. */
export interface ConfigValidationError {
  loc?: Array<string | number>;
  msg?: string;
}

const KIND_BY_STATUS: Record<number, ConfigSaveErrorKind> = {
  400: 'missing_hash',
  403: 'forbidden',
  409: 'conflict',
  422: 'invalid',
};

const DEFAULT_MESSAGES: Record<ConfigSaveErrorKind, string> = {
  missing_hash: 'The configuration could not be saved.',
  forbidden: 'You are not allowed to change these settings.',
  conflict: 'The configuration was changed by someone else. Reload and apply your change again.',
  invalid: 'The configuration is invalid.',
};

export class ConfigSaveError extends Error {
  readonly status: number;
  readonly kind: ConfigSaveErrorKind;
  /** The server's `detail` string (English). */
  readonly serverDetail?: string;
  readonly validationErrors: ConfigValidationError[];

  constructor(status: number, kind: ConfigSaveErrorKind, serverDetail?: string, validationErrors: ConfigValidationError[] = []) {
    super(i18n.t(`configSave.${kind}`, { ns: 'tenant', defaultValue: DEFAULT_MESSAGES[kind] }));
    this.name = 'ConfigSaveError';
    this.status = status;
    this.kind = kind;
    this.serverDetail = serverDetail;
    this.validationErrors = validationErrors;
  }
}

export const isConfigSaveError = (err: unknown): err is ConfigSaveError => err instanceof ConfigSaveError;

/**
 * Map an axios error from the save to a `ConfigSaveError`, or null when it isn't
 * one of the save's own refusals. A FastAPI request-validation 422 (array
 * `detail`) is not mapped: callers read that shape inline already.
 */
export function toConfigSaveError(error: any): ConfigSaveError | null {
  const status: number | undefined = error?.response?.status;
  const kind = status !== undefined ? KIND_BY_STATUS[status] : undefined;
  if (!status || !kind) return null;
  const data = error.response.data ?? {};
  if (Array.isArray(data.detail)) return null;
  const detail = typeof data.detail === 'string' ? data.detail : undefined;
  const validationErrors = Array.isArray(data.validation_errors) ? data.validation_errors : [];
  return new ConfigSaveError(status, kind, detail, validationErrors);
}

/** `loc.path: msg` lines for display. */
export function formatValidationErrors(errors: ConfigValidationError[]): string[] {
  return errors.map(e => {
    const loc = Array.isArray(e.loc) ? e.loc.join('.') : '';
    const msg = e.msg ?? '';
    return loc ? `${loc}: ${msg}` : msg;
  });
}
